// Utility function to safely parse hardware entries in donations
// This fixes the "Invalid JSON format for hardware" error that can occur 
// when hardware field has a simple string value instead of JSON

/**
 * Safely parses the hardware field from a donation
 * @param {*} hardwareField - The hardware field to parse (string or array)
 * @returns {Array} - A properly formatted hardware array
 */
export function safeParseHardware(hardwareField) {
  try {
    if (!hardwareField) {
      return [];
    }
    
    // If it's already an array, return it
    if (Array.isArray(hardwareField)) {
      return hardwareField;
    }
    
    // If it's a string, try to parse it as JSON
    if (typeof hardwareField === 'string') {
      // Check if it looks like JSON
      if (hardwareField.trim().startsWith('[') || hardwareField.trim().startsWith('{')) {
        try {
          return JSON.parse(hardwareField);
        } catch (err) {
          console.warn('Invalid JSON format for hardware:', hardwareField);
          return [{ type: hardwareField, quantity: 1 }];
        }
      } else {
        // If it doesn't look like JSON, treat it as a simple string description
        return [{ type: hardwareField, quantity: 1 }];
      }
    }
    
    // If it's an object but not an array, wrap it in an array
    if (typeof hardwareField === 'object') {
      return [hardwareField];
    }
    
    // Fallback for any other cases
    return [{ type: String(hardwareField), quantity: 1 }];
  } catch (err) {
    console.error('Error parsing hardware:', err);
    return [{ type: 'Unknown hardware', quantity: 1 }];
  }
}

/**
 * Gets a displayable hardware name from a donation
 * @param {*} donation - The donation object
 * @returns {string} - A displayable hardware type name
 */
export function getHardwareDisplayName(donation) {
  if (!donation || !donation.hardware) {
    return 'No hardware';
  }
  
  const parsedHardware = safeParseHardware(donation.hardware);
  
  if (parsedHardware.length === 0) {
    return 'No hardware specified';
  }
  
  if (parsedHardware.length === 1) {
    return parsedHardware[0].type || 'Hardware';
  }
  
  return `${parsedHardware[0].type || 'Hardware'} (+${parsedHardware.length - 1} more)`;
}

/**
 * Calculates the total quantity of hardware items in a donation
 * @param {*} donation - The donation object
 * @returns {number} - The total quantity
 */
export function getTotalHardwareQuantity(donation) {
  if (!donation || !donation.hardware) {
    return 0;
  }
  
  const parsedHardware = safeParseHardware(donation.hardware);
  
  return parsedHardware.reduce((total, item) => {
    const quantity = parseInt(item.quantity) || 1;
    return total + quantity;
  }, 0);
}

export default {
  safeParseHardware,
  getHardwareDisplayName,
  getTotalHardwareQuantity
};
